import {useEffect, useMemo, useRef, useState} from 'react';

import {
  apiRunItcTraderApplication,
  apiSubmitTraderFormFields,
  apiUpdateFdApplicationProgress,
  ISubmitTraderFormFieldInput,
} from 'constants/Api';
import {ITradingFormField} from 'constants/interfaces';

import {lshMapTraderForm} from '../../tradingFormInlineUtils';
import {useSupplierApplicationJourney} from '../../SupplierApplicationJourneyContext';
import {
  lshExtractBankingDetailsFromTraderApplication,
  lshFlattenTraderApplicationFields,
  lshGetTraderFieldLabel,
  lshGetTraderRegistrationField,
  lshNormaliseEntityRegistrationInput,
  lshValidateTraderRegistrationInput,
} from '../../tradingFormJourneyUtils';
import type {TradingFormSectionInlineHandle} from './components';
import {
  BankingFieldKey,
  CompanyRelatedParty,
  CompanySubStepKey,
  IUseCompanyDetailsStepResult,
} from './companyDetails.types';
import {
  COMPANY_PROGRESS_STEP,
  DEFAULT_BBBEE_OPTIONS,
  lshExtractBbbeeLevel,
  lshExtractCompanyType,
  lshExtractRelatedParties,
  lshExtractRegistrationNumber,
  lshExtractSingleChoiceOptions,
  lshFieldId,
  lshFieldTitle,
  lshFindFirstField,
  lshHasAny,
  lshNormaliseBbbeeLevel,
  lshResolveBbbeeValueFromOptions,
  lshResolveSelectValueFromOptions,
  lshToEditableText,
} from './companyDetails.utils';

export function lshUseCompanyDetailsStep(): IUseCompanyDetailsStepResult {
  const {
    application,
    traderApplication,
    companySummary,
    activeStep,
    activeSubStepIndex,
    goPrevious,
    goNext,
    goToSubStep,
    refreshJourney,
    setApplication,
  } = useSupplierApplicationJourney();

  const companySubSteps = activeStep.subSteps || [];
  const activeSubStep = companySubSteps[activeSubStepIndex];
  const activeSubStepKey = (activeSubStep?.key ||
    'registration') as CompanySubStepKey;

  const [registrationNumber, setRegistrationNumber] = useState('');
  const [registrationError, setRegistrationError] = useState<string | null>(
    null,
  );
  const [bbbeeLevel, setBbbeeLevel] = useState('');
  const [bankInputs, setBankInputs] = useState({
    bankName: '',
    branchCode: '',
    accountType: '',
    accountNumber: '',
  });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [busyReportId, setBusyReportId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const detailsFormRef = useRef<TradingFormSectionInlineHandle | null>(null);

  const registrationField = useMemo(
    () =>
      lshGetTraderRegistrationField(traderApplication) as
        | ITradingFormField
        | undefined,
    [traderApplication],
  );

  const registrationFieldLabel = useMemo(
    () =>
      lshGetTraderFieldLabel(registrationField) ||
      'Company registration number',
    [registrationField],
  );

  const companyType = useMemo(
    () =>
      lshExtractCompanyType(
        traderApplication,
        application?.company?.companyType?.name,
      ),
    [application?.company?.companyType?.name, traderApplication],
  );

  const mappedCompanySections = useMemo(() => {
    const forms = traderApplication?.forms || [];
    const companyForm =
      forms.find((form) =>
        String(form?.id || '').includes('private_company_form'),
      ) || forms[0];

    if (!companyForm) return [];

    try {
      return (lshMapTraderForm(companyForm as any)?.sections || []) as {
        id: string;
        name: string;
        fields: ITradingFormField[];
      }[];
    } catch {
      return (companyForm.sections || []) as {
        id: string;
        name: string;
        fields: ITradingFormField[];
      }[];
    }
  }, [traderApplication]);

  const companyDetailsSection = useMemo(
    () =>
      mappedCompanySections.find((section) =>
        String(section?.id || '').includes('company_details'),
      ) ||
      mappedCompanySections.find((section) =>
        String(section?.name || '')
          .toLowerCase()
          .includes('company details'),
      ) ||
      null,
    [mappedCompanySections],
  );

  const flattenedTraderFields = useMemo(
    () =>
      (lshFlattenTraderApplicationFields(
        traderApplication,
      ) as unknown as ITradingFormField[]) || [],
    [traderApplication],
  );

  const bbbeeField = useMemo(() => {
    const findInFields = (
      fields?: ITradingFormField[],
    ): ITradingFormField | undefined => {
      for (const field of fields || []) {
        if (!field) continue;
        if (
          String((field as any).__typename || '') !==
          'TradingFormSingleChoiceOption'
        ) {
          continue;
        }

        const id = lshFieldId(field);
        const title = lshFieldTitle(field);
        if (id.includes('requirements')) continue;
        if (
          lshHasAny(id, ['bbee_level', 'bbbee', 'bbee']) ||
          lshHasAny(title, ['b-bbee', 'bbbee', 'bbee'])
        ) {
          return field;
        }
      }
      return undefined;
    };

    for (const section of mappedCompanySections) {
      const match = findInFields(
        (section?.fields || []) as ITradingFormField[],
      );
      if (match) return match;
    }

    for (const section of mappedCompanySections) {
      const sectionId = String(section?.id || '').toLowerCase();
      if (!lshHasAny(sectionId, ['bbee', 'bbbee'])) continue;

      const fields = (section?.fields || []) as ITradingFormField[];
      const exact = fields.find((field) => {
        const id = lshFieldId(field);
        return (
          String((field as any).__typename || '') ===
            'TradingFormSingleChoiceOption' &&
          id.endsWith('_bbee_level') &&
          !id.includes('requirements')
        );
      });
      if (exact) return exact;

      const loose = fields.find((field) => {
        const id = lshFieldId(field);
        return (
          String((field as any).__typename || '') ===
            'TradingFormSingleChoiceOption' &&
          id.includes('bbee_level') &&
          !id.includes('requirements')
        );
      });
      if (loose) return loose;
    }

    return findInFields(flattenedTraderFields);
  }, [flattenedTraderFields, mappedCompanySections]);

  const bbbeeOptions = useMemo(() => {
    const options = lshExtractSingleChoiceOptions(bbbeeField);
    if (!options.length) return DEFAULT_BBBEE_OPTIONS;
    return options;
  }, [bbbeeField]);

  const defaultBbbeeValue = useMemo(() => {
    const currentValue = lshToEditableText(
      (bbbeeField as any)?.value ??
        (bbbeeField as any)?.singleChoiceOptionValue,
    );
    if (currentValue) {
      return lshResolveBbbeeValueFromOptions(currentValue, bbbeeOptions);
    }

    const summaryValue =
      lshExtractBbbeeLevel(traderApplication) ||
      lshNormaliseBbbeeLevel(companySummary.bbbeeStatus);
    return lshResolveBbbeeValueFromOptions(summaryValue, bbbeeOptions);
  }, [bbbeeField, bbbeeOptions, companySummary.bbbeeStatus, traderApplication]);

  const bbbeeFieldType = useMemo(
    () =>
      String((bbbeeField as any)?.__typename || '') ||
      'TradingFormSingleChoiceOption',
    [bbbeeField],
  );

  const bankingFormFields = useMemo(() => {
    const findBankField = (
      idTokens: string[],
      labelTokens: string[] = [],
    ): ITradingFormField | undefined =>
      lshFindFirstField(flattenedTraderFields, (field) => {
        const id = lshFieldId(field);
        const title = lshFieldTitle(field);
        return lshHasAny(id, idTokens) || lshHasAny(title, labelTokens);
      });

    return {
      bankName: findBankField(
        ['account_details_bank', '_bank'],
        ['bank name', 'bank'],
      ),
      branchCode: findBankField(
        ['account_details_branch_code', 'branch_code'],
        ['branch code'],
      ),
      accountType: findBankField(
        ['account_details_account_type', 'account_type'],
        ['account type'],
      ),
      accountNumber: findBankField(
        ['account_details_account_number', 'account_number'],
        ['account number'],
      ),
    };
  }, [flattenedTraderFields]);

  const bankNameOptions = useMemo(
    () => lshExtractSingleChoiceOptions(bankingFormFields.bankName),
    [bankingFormFields.bankName],
  );

  const accountTypeOptions = useMemo(
    () => lshExtractSingleChoiceOptions(bankingFormFields.accountType),
    [bankingFormFields.accountType],
  );

  const relatedParties = useMemo(
    () =>
      lshExtractRelatedParties(
        traderApplication,
        companySummary.relatedParties,
      ),
    [companySummary.relatedParties, traderApplication],
  );

  const bankingFromTraderForm = useMemo(
    () => lshExtractBankingDetailsFromTraderApplication(traderApplication),
    [traderApplication],
  );

  useEffect(() => {
    setRegistrationNumber(
      lshNormaliseEntityRegistrationInput(
        lshExtractRegistrationNumber(traderApplication) ||
          companySummary.registrationNumber,
        companyType,
      ),
    );
    setRegistrationError(null);
  }, [companySummary.registrationNumber, companyType, traderApplication]);

  useEffect(() => {
    setBbbeeLevel(defaultBbbeeValue);
  }, [defaultBbbeeValue]);

  useEffect(() => {
    setBankInputs({
      bankName: lshResolveSelectValueFromOptions(
        lshToEditableText((bankingFormFields.bankName as any)?.value) ||
          lshToEditableText(
            (bankingFormFields.bankName as any)?.textInputValue,
          ) ||
          lshToEditableText(
            (bankingFormFields.bankName as any)?.singleChoiceOptionValue,
          ) ||
          lshToEditableText(bankingFromTraderForm.bankName) ||
          lshToEditableText(companySummary.bankName),
        bankNameOptions,
      ),
      branchCode:
        lshToEditableText((bankingFormFields.branchCode as any)?.value) ||
        lshToEditableText(
          (bankingFormFields.branchCode as any)?.textInputValue,
        ) ||
        lshToEditableText(
          (bankingFormFields.branchCode as any)?.singleChoiceOptionValue,
        ) ||
        lshToEditableText(bankingFromTraderForm.branchCode) ||
        lshToEditableText(companySummary.bankBranchCode),
      accountType: lshResolveSelectValueFromOptions(
        lshToEditableText((bankingFormFields.accountType as any)?.value) ||
          lshToEditableText(
            (bankingFormFields.accountType as any)?.textInputValue,
          ) ||
          lshToEditableText(
            (bankingFormFields.accountType as any)?.singleChoiceOptionValue,
          ) ||
          lshToEditableText(bankingFromTraderForm.accountType) ||
          lshToEditableText(companySummary.bankAccountType),
        accountTypeOptions,
      ),
      accountNumber:
        lshToEditableText((bankingFormFields.accountNumber as any)?.value) ||
        lshToEditableText(
          (bankingFormFields.accountNumber as any)?.textInputValue,
        ) ||
        lshToEditableText(
          (bankingFormFields.accountNumber as any)?.singleChoiceOptionValue,
        ) ||
        lshToEditableText(bankingFromTraderForm.accountNumber) ||
        lshToEditableText(companySummary.bankAccountNumber),
    });
  }, [
    bankingFormFields.accountNumber,
    bankingFormFields.accountType,
    bankingFormFields.bankName,
    bankingFormFields.branchCode,
    bankNameOptions,
    bankingFromTraderForm.accountNumber,
    bankingFromTraderForm.accountType,
    bankingFromTraderForm.bankName,
    bankingFromTraderForm.branchCode,
    accountTypeOptions,
    companySummary.bankAccountNumber,
    companySummary.bankAccountType,
    companySummary.bankBranchCode,
    companySummary.bankName,
  ]);

  const handleRefresh = async () => {
    if (isRefreshing || isSubmitting) return;

    setIsRefreshing(true);
    setError(null);
    setNotice(null);
    setDetailsError(null);

    try {
      await refreshJourney();
      setNotice('Latest supplier application details loaded.');
    } catch (refreshError: any) {
      setError(
        refreshError?.message ||
          'We could not refresh the company details just now. Please try again.',
      );
    } finally {
      setIsRefreshing(false);
    }
  };

  const submitTraderFieldChanges = async (
    updates: ISubmitTraderFormFieldInput[],
  ) => {
    if (!updates.length) return;
    if (!traderApplication?.id) {
      throw new Error(
        'Could not find the supplier trading application linked to this form. Refresh data and try again.',
      );
    }

    const response = await apiSubmitTraderFormFields({
      applicationId: traderApplication.id,
      fields: updates,
      companyEntityType: true,
    });

    if (!response?.success) {
      throw new Error(
        response?.message || 'We couldn’t save these company updates.',
      );
    }
  };

  const submitDetailsChanges = async (
    fields: ISubmitTraderFormFieldInput[],
  ) => {
    if (!fields.length) return;

    setIsSubmitting(true);
    setDetailsError(null);
    setError(null);
    setNotice(null);

    try {
      await submitTraderFieldChanges(fields);
      await refreshJourney();
      setNotice('Company details updated.');
    } catch (submitError: any) {
      const message =
        submitError?.message || 'We couldn’t save these company details.';
      setDetailsError(message);
      throw submitError;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadPartyCreditReport = async (
    party: CompanyRelatedParty,
  ) => {
    if (busyReportId) return;
    if (!party.idNumber || !party.title) {
      setError(
        'Missing related-party details. Please make sure the party has a full name and ID number first.',
      );
      return;
    }

    const [name, ...surnameParts] = party.title.split(' ');
    const surname = surnameParts.join(' ').trim() || name;

    setBusyReportId(party.id);
    setError(null);
    setNotice(null);

    try {
      const report = await apiRunItcTraderApplication({
        idNumber: party.idNumber,
        name,
        surname,
        externalServiceRequest: true,
      });

      const reportUrl = String(report?.finalDocLink ?? '').trim();

      if (!reportUrl) {
        throw new Error('No credit report link returned.');
      }

      window.open(reportUrl, '_blank', 'noopener,noreferrer');
    } catch (downloadError: any) {
      setError(
        downloadError?.message ||
          'Unable to download the related-party credit report. Please try again.',
      );
    } finally {
      setBusyReportId(null);
    }
  };

  const handleRegistrationChange = (value: string) => {
    setRegistrationError(null);
    setRegistrationNumber(
      lshNormaliseEntityRegistrationInput(value, companyType),
    );
  };

  const handleBbbeeLevelChange = (value: string) => {
    setBbbeeLevel(value);
  };

  const handleBankInputChange = (key: BankingFieldKey, value: string) => {
    setBankInputs((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const applyUpdatesAndRefresh = async (
    updates: ISubmitTraderFormFieldInput[],
    successMessage?: string,
  ) => {
    if (!updates.length) return;

    setIsSubmitting(true);
    try {
      await submitTraderFieldChanges(updates);
      await refreshJourney();
      if (successMessage) setNotice(successMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const persistCompanyProgress = async (completedSubSteps: number) => {
    const numericApplicationId = Number(application?.id);
    if (!Number.isFinite(numericApplicationId)) return;

    const completed = Boolean(application?.completed);
    const currentProgress = Number(application?.progress ?? 0);
    const targetProgress = completed
      ? COMPANY_PROGRESS_STEP
      : Number.isFinite(currentProgress) &&
        currentProgress > COMPANY_PROGRESS_STEP
      ? currentProgress
      : COMPANY_PROGRESS_STEP;
    const subProgress = companySubSteps.length
      ? Math.max(
          0,
          Math.min(100, (completedSubSteps / companySubSteps.length) * 100),
        )
      : 0;

    const updated = await apiUpdateFdApplicationProgress({
      applicationId: numericApplicationId,
      completed,
      progress: targetProgress,
      subProgress,
    });

    setApplication((current) => {
      if (!current) return current;

      return {
        ...current,
        completed: updated?.completed ?? completed,
        progress: updated?.progress ?? targetProgress,
        subProgress: updated?.subProgress ?? subProgress,
        applicationState: updated?.applicationState ?? current.applicationState,
      };
    });
  };

  const handleNext = async () => {
    if (isSubmitting || isRefreshing) return;

    setError(null);
    setNotice(null);
    setDetailsError(null);

    try {
      if (activeSubStepKey === 'registration') {
        const nextRegistration = lshNormaliseEntityRegistrationInput(
          registrationNumber,
          companyType,
        );
        const validationError = lshValidateTraderRegistrationInput({
          entityTypeTitle: companyType,
          field: registrationField,
          value: nextRegistration,
        });
        if (validationError) {
          setRegistrationError(validationError);
          return;
        }

        setRegistrationError(null);
        setRegistrationNumber(nextRegistration);
        const currentRegistration = lshNormaliseEntityRegistrationInput(
          lshExtractRegistrationNumber(traderApplication) ||
            companySummary.registrationNumber,
          companyType,
        );
        const registrationFieldType =
          String((registrationField as any)?.__typename || '') ||
          'TradingFormTextInput';

        if (
          registrationField?.id &&
          nextRegistration &&
          nextRegistration !== currentRegistration
        ) {
          await applyUpdatesAndRefresh(
            [
              {
                id: String(registrationField.id),
                value: nextRegistration,
                fieldTypeName: registrationFieldType,
              },
            ],
            'Company registration updated.',
          );
        }
      }

      if (activeSubStepKey === 'details') {
        if (!companyDetailsSection) {
          setError(
            'We couldn’t find a company details form for this profile yet. Refresh data and try again.',
          );
          return;
        }

        const saved = await detailsFormRef.current?.submit();
        if (!saved) {
          return;
        }
      }

      if (activeSubStepKey === 'bbbee') {
        if (!bbbeeLevel) {
          setError('Select the B-BBEE status to continue.');
          return;
        }

        if (!bbbeeField?.id) {
          setError(
            'No B-BBEE field is linked to this profile yet. Refresh data and try again.',
          );
          return;
        }

        const currentBbbee = lshToEditableText(
          (bbbeeField as any)?.value ??
            (bbbeeField as any)?.singleChoiceOptionValue,
        );
        if (bbbeeLevel !== currentBbbee) {
          await applyUpdatesAndRefresh(
            [
              {
                id: String(bbbeeField.id),
                value: bbbeeLevel,
                fieldTypeName: bbbeeFieldType,
              },
            ],
            'B-BBEE status updated.',
          );
        }
      }

      if (activeSubStepKey === 'banking') {
        if (
          !bankInputs.bankName.trim() ||
          !bankInputs.branchCode.trim() ||
          !bankInputs.accountType.trim() ||
          !bankInputs.accountNumber.trim()
        ) {
          setError('Enter the full banking details to continue.');
          return;
        }

        const updates: ISubmitTraderFormFieldInput[] = [];
        const appendBankingUpdate = (
          field: ITradingFormField | undefined,
          value: string,
        ) => {
          if (!field?.id) return;

          const previous = lshToEditableText((field as any)?.value);
          if (previous === value) return;

          updates.push({
            id: String(field.id),
            value,
            fieldTypeName:
              String((field as any)?.__typename || '') ||
              'TradingFormTextInput',
          });
        };

        appendBankingUpdate(
          bankingFormFields.bankName,
          bankInputs.bankName.trim(),
        );
        appendBankingUpdate(
          bankingFormFields.branchCode,
          bankInputs.branchCode.trim(),
        );
        appendBankingUpdate(
          bankingFormFields.accountType,
          bankInputs.accountType.trim(),
        );
        appendBankingUpdate(
          bankingFormFields.accountNumber,
          bankInputs.accountNumber.trim(),
        );

        if (updates.length > 0) {
          await applyUpdatesAndRefresh(updates, 'Banking details updated.');
        }
      }

      await persistCompanyProgress(activeSubStepIndex + 1);
      goNext();
    } catch (nextError: any) {
      setError(
        nextError?.message ||
          'We couldn’t save this section right now. Please try again.',
      );
    }
  };

  return {
    application,
    traderApplication,
    activeStep,
    companySubSteps,
    activeSubStep,
    activeSubStepIndex,
    activeSubStepKey,
    goPrevious,
    goNext,
    goToSubStep,
    registrationField,
    registrationFieldLabel,
    registrationNumber,
    registrationError,
    companyType,
    companyDetailsSection,
    detailsFormRef,
    detailsError,
    bbbeeLevel,
    bbbeeOptions,
    bankInputs,
    bankNameOptions,
    accountTypeOptions,
    relatedParties,
    busyReportId,
    isRefreshing,
    isSubmitting,
    error,
    notice,
    submitDetailsChanges,
    handleRefresh,
    handleNext,
    handleRegistrationChange,
    handleBankInputChange,
    handleBbbeeLevelChange,
    handleDownloadPartyCreditReport,
  };
}
